import React, { useEffect } from 'react';

/**
 * Bottom sheet on mobile, centered card on desktop. Used for route-modals
 * (product detail / cart overlay) and for small in-page dialogs (support,
 * confirmations). Closing always goes through `onClose` — callers that are
 * mounted on a route additionally call navigate(-1) there so the back
 * button and the X button behave identically.
 */
export default function Sheet({ open, onClose, children, maxWidth = 480, labelledBy }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[300] flex items-end md:items-center justify-center bg-ink/55 backdrop-blur-[2px] animate-fadeIn"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        onClick={(e) => e.stopPropagation()}
        className="w-full bg-white rounded-t-3xl md:rounded-3xl shadow-lift max-h-[92vh] md:max-h-[85vh] overflow-y-auto animate-slideUp"
        style={{ maxWidth }}
      >
        {children}
      </div>
    </div>
  );
}

export function SheetHeader({ title, sub, onClose, emoji }) {
  return (
    <div className="sticky top-0 bg-white/95 backdrop-blur px-5 py-4 flex items-start justify-between border-b border-black/5 z-10">
      <div>
        <div className="font-black text-[17px] text-brand-dark">{emoji ? `${emoji} ` : ''}{title}</div>
        {sub ? <div className="text-xs text-ink-faint mt-0.5">{sub}</div> : null}
      </div>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="shrink-0 h-8 w-8 rounded-full bg-red-50 text-red-600 font-black text-sm flex items-center justify-center hover:bg-red-100"
        >
          ✕
        </button>
      ) : null}
    </div>
  );
}

export function SheetBody({ children, className = '' }) {
  return <div className={`px-5 pt-4 pb-8 ${className}`}>{children}</div>;
}
